import type { ComponentType, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { useNow } from '@/hooks/useNow';
import { outcomeLabel } from '@/services/dialer/callOutcomeLabels';
import { Badge } from '@/components/ui/badge';
import { Loader2, Phone, Activity, CheckCircle2, PhoneOff, Voicemail, XCircle, CalendarCheck } from 'lucide-react';
import { getEffectiveOutcome, hasMeetingBooked } from '@/utils/callUtils';
import LineTranscriptPreview from './call-panel/LineTranscriptPreview';
import CallScreeningTag from './CallScreeningTag';

// Drapeau "RDV booké" — séparé du statut d'appel (2026-05-21). S'affiche en
// plus du badge de statut, jamais à sa place.
function MeetingFlag() {
  return (
    <Badge className="bg-emerald-600 text-white border-0 px-2 py-1 text-xs font-semibold flex items-center gap-1">
      <CalendarCheck className="w-3.5 h-3.5" />
      RDV
    </Badge>
  );
}

type IconType = ComponentType<SVGProps<SVGSVGElement> & { className?: string }>;

interface StatusMeta {
  color: string;
  icon: IconType;
  animate: boolean;
  showDuration: boolean;
}

interface OutcomeMeta {
  color: string;
  icon: IconType;
}

// Palette cohérente — 4 familles sémantiques :
//   • slate  → neutre/passé (terminé, annulé, sans réponse)
//   • sky    → info/en cours (numérotation, sonnerie, contacté)
//   • emerald→ succès (en ligne, RDV, humain détecté)
//   • amber  → attention (occupé, rappel demandé)
// Tons doux (50 bg / 700 text) + ring-inset pour la profondeur sans agression.
const statusConfig: Record<string, StatusMeta> = {
  dialing: { color: 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200', icon: Loader2, animate: true, showDuration: true },
  ringing: { color: 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200', icon: Phone, animate: true, showDuration: true },
  ai_analyzing: { color: 'bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200', icon: Activity, animate: true, showDuration: true },
  human_detected: { color: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200', icon: CheckCircle2, animate: false, showDuration: true },
  connected_to_agent: { color: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200', icon: CheckCircle2, animate: false, showDuration: true },
  completed: { color: 'bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-200', icon: CheckCircle2, animate: false, showDuration: false },
  failed: { color: 'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200', icon: XCircle, animate: false, showDuration: false },
  busy: { color: 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200', icon: PhoneOff, animate: false, showDuration: false },
  no_answer: { color: 'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200', icon: PhoneOff, animate: false, showDuration: false },
  voicemail: { color: 'bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-200', icon: Voicemail, animate: false, showDuration: false },
  voicemail_playing: { color: 'bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200', icon: Voicemail, animate: true, showDuration: true },
  canceled: { color: 'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200', icon: XCircle, animate: false, showDuration: false },
};

const outcomeConfig: Record<string, OutcomeMeta> = {
  meeting_booked: { color: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200', icon: CheckCircle2 },
  contacted: { color: 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200', icon: CheckCircle2 },
  not_interested: { color: 'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200', icon: XCircle },
  callback_requested: { color: 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200', icon: Phone },
  not_reachable: { color: 'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200', icon: PhoneOff },
};

interface CallShape {
  id?: string | null;
  call_status?: string | null;
  connected_at?: string | Date | null;
  started_at?: string | Date | null;
  voicemail_transcription?: string | null;
  meeting_booked?: boolean | null;
  call_outcome?: string | null;
  amd_result?: string | null;
}

// Statuts d'un appel encore "vivant" → on affiche l'aperçu transcript en ligne.
const LIVE_STATUSES = new Set([
  'dialing', 'ringing', 'ai_analyzing', 'human_detected', 'connected_to_agent',
]);

interface InlineCallStatusProps {
  call?: CallShape | null;
  /** Unused since 2026-10-07: the duration ticks on its own clock (useNow). */
  currentTime?: Date | number;
}

export default function InlineCallStatus({ call }: InlineCallStatusProps) {
  const { t } = useTranslation('dialer');
  const currentTime = useNow(1000, !!call && LIVE_STATUSES.has(String(call.call_status)));
  if (!call) return null;

  const meetingBooked = hasMeetingBooked(call);

  if (call.call_status === 'completed') {
    const effectiveOutcome = getEffectiveOutcome(call as never);
    const oc = outcomeConfig[effectiveOutcome];
    if (oc) {
      const OutcomeIcon = oc.icon;
      return (
        <div className="flex items-center gap-2">
          <Badge className={`${oc.color} border-0 px-3 py-1 text-xs font-semibold flex items-center gap-1.5`}>
            <OutcomeIcon className="w-3.5 h-3.5" />
            {outcomeLabel(effectiveOutcome, t)}
          </Badge>
          {meetingBooked && <MeetingFlag />}
          <CallScreeningTag call={call} />
        </div>
      );
    }
    // Outcome inconnu mais RDV booké : afficher au moins le drapeau.
    if (meetingBooked) {
      return (
        <div className="flex items-center gap-2">
          <MeetingFlag />
        </div>
      );
    }
  }

  const config = call.call_status ? statusConfig[call.call_status] : undefined;
  if (!config) return null;

  const StatusIcon = config.icon;
  const startTs = call.connected_at || call.started_at;
  const nowMs = currentTime;
  const duration = startTs ? Math.floor((nowMs - new Date(startTs).getTime()) / 1000) : 0;
  const minutes = Math.floor(duration / 60);
  const seconds = duration % 60;

  const vmTranscription =
    (call.call_status === 'voicemail_playing' || call.call_status === 'voicemail') &&
    call.voicemail_transcription;

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-2">
        <Badge className={`${config.color} border-0 px-3 py-1 text-xs font-semibold flex items-center gap-1.5`}>
          <StatusIcon className={`w-3.5 h-3.5 ${config.animate ? 'animate-spin' : ''}`} />
          {outcomeLabel(call.call_status, t)}
        </Badge>
        {meetingBooked && <MeetingFlag />}
        <CallScreeningTag call={call} />
        {config.showDuration && (
          <span className="text-xs text-muted-foreground font-data">
            {minutes}:{seconds.toString().padStart(2, '0')}
          </span>
        )}
      </div>
      {vmTranscription && (
        <p
          className="text-[11px] text-purple-600 italic truncate max-w-[200px]"
          title={call.voicemail_transcription ?? undefined}
        >
          {call.voicemail_transcription}
        </p>
      )}
      {/* Aperçu transcript live (style Minari) — premières secondes, pour les
          appels encore en cours. Le flux se ferme seul après ~10s. */}
      {call.id && LIVE_STATUSES.has(call.call_status ?? '') && (
        <LineTranscriptPreview callId={call.id} />
      )}
    </div>
  );
}
